import { apiEmploymentContracts } from "@/api/employment-contracts.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmploymentContractSavePayload } from "@/types/employment-contracts.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { useMemo, useState } from "react";
import { SelectItem } from "@skailer-com/uikit";
import { escapeRegExp } from "lodash";

const employmentContractsKey = createQueryKeys("employmentContracts", {
  all: null,
});

export const useEmploymentContracts = (enabled: boolean = true) => {
  return useQuery({
    ...employmentContractsKey.all,
    queryFn: async () => {
      const result = await apiEmploymentContracts.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useEmploymentContractsSearchInline = (enabled: boolean = true) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useEmploymentContracts(enabled);

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");

    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export interface SaveParams {
  id: number | undefined;
  payload: ApiEmploymentContractSavePayload;
}

export const useEmploymentContractsSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveParams) => {
      const result = id
        ? await apiEmploymentContracts.update(id, payload)
        : await apiEmploymentContracts.create(payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentContractsKey.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmploymentContractsDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiEmploymentContracts.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentContractsKey.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};
